import { FC, ReactNode } from "react";

import {
  PUNCHUP_ICON_SIZE,
  PunchupIcons,
  type PunchupIconName,
} from "../../../icons";
import { cx } from "../../../utils/cx";
import styles from "./PunchupAppBanner.module.css";

export type PunchupAppBannerStatus = "critical" | "warning" | "info";

export interface PunchupAppBannerProps {
  /** The tone. `critical` is announced as an alert; the others as a polite status. */
  status: PunchupAppBannerStatus;
  /** The banner copy. Any node, so an inline `<a>` is allowed and renders underlined. */
  children: ReactNode;
  /** Hides the status icon; app-level copy may stand alone. */
  hideIcon?: boolean;
  /** Renders the dismiss (×) button; the consumer owns the dismissed state. */
  onDismiss?: () => void;
  /** Accessible name of the dismiss button. */
  dismissLabel?: string;
}

const STATUS_ICON: Record<PunchupAppBannerStatus, PunchupIconName> = {
  critical: "alert",
  warning: "warning",
  info: "info",
};

const STATUS_CLASS: Record<PunchupAppBannerStatus, string> = {
  critical: styles.critical,
  warning: styles.warning,
  info: styles.info,
};

/**
 * The app-level banner alert: a solid, full-width bar for system-level
 * messages. It spans the top edge of the app and pushes the app container
 * down by its own height. Show one at a time.
 */
export const PunchupAppBanner: FC<PunchupAppBannerProps> = ({
  status,
  children,
  hideIcon = false,
  onDismiss,
  dismissLabel = "Dismiss",
}) => {
  const Icon = PunchupIcons[STATUS_ICON[status]];

  return (
    <div
      className={cx(
        styles.banner,
        STATUS_CLASS[status],
        onDismiss && styles.dismissible
      )}
      role={status === "critical" ? "alert" : "status"}
      data-status={status}
    >
      {!hideIcon && (
        <Icon
          className={styles.icon}
          size={PUNCHUP_ICON_SIZE.lg}
          aria-hidden="true"
        />
      )}
      <div className={styles.copy}>{children}</div>
      {onDismiss && (
        <button
          type="button"
          className={styles.dismiss}
          onClick={onDismiss}
          aria-label={dismissLabel}
        >
          <PunchupIcons.clear size={PUNCHUP_ICON_SIZE.md} aria-hidden="true" />
        </button>
      )}
    </div>
  );
};
